import { useRecoilValue, useSetRecoilState } from "recoil";
import { cbContactPersonToDeleteArrayState, cbErrorSectionValidationState, isGlobalEditModeFormDirtyState, isGlobalEditModeState, queryFieldsState } from "../state/customerbaseState";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { useTranslation } from "react-i18next";
import { CBAuth, CBExtraAddressAction, CBExtraAddressDeltaFields, CBPartnerType, IFormValues } from "../types";
import { useFormContext, useFormState } from "react-hook-form";
import { customerFormToEditCustomerParams } from "../utils";
import { useEffect } from "react";
import { useHasScope } from "../../../config/globalConfig";

const CBMultiSectionActionButtons = () => {

    const hasScope = useHasScope;
    const { t } = useTranslation("customerbase");
    const { isDirty, defaultValues } = useFormState<IFormValues>();
    const { getValues, reset, trigger } = useFormContext<IFormValues>();
    const isGlobalEditMode = useRecoilValue(isGlobalEditModeState);
    const setIsGlobalEditMode = useSetRecoilState(isGlobalEditModeState);

    const setIsGlobalEditModeFormDirty = useSetRecoilState(isGlobalEditModeFormDirtyState);

    const setErrorSection = useSetRecoilState(cbErrorSectionValidationState);

    const cbContactPersonToDeleteArray = useRecoilValue(cbContactPersonToDeleteArrayState);
    const setCbContactPersonToDeleteArray = useSetRecoilState(cbContactPersonToDeleteArrayState);
    
    const setQueryFields = useSetRecoilState(queryFieldsState);

    useEffect(() => {

        if(!isGlobalEditMode) setIsGlobalEditModeFormDirty(false);
        else if(isDirty) setIsGlobalEditModeFormDirty(true);
        
    }, [isGlobalEditMode, isDirty]);

    const onSubmit = (params: any) => {
        
        var extraAddressesDelta: CBExtraAddressDeltaFields[] = [];
        const cbCustomerId = getValues("cbDetails.cbCustomerId");

        const defaultExtraAddresses = defaultValues?.cbExtraAddresses || [];
        const currentExtraAddresses = getValues("cbExtraAddresses");
        
        defaultExtraAddresses.forEach(def => {
            if(!!def && (currentExtraAddresses.findIndex(curr => !!curr.idAssociation && (curr.idAssociation == def.idAssociation)) == -1)) {
                extraAddressesDelta.push({
                    action: CBExtraAddressAction.UNLINK,
                    link: {
                        customerId: cbCustomerId,
                        customerIdToLink: def.customerId || null,
                    }
                });
            }
        });

        currentExtraAddresses.filter(curr => !curr.idAssociation).forEach(curr => {
            extraAddressesDelta.push({
                action: CBExtraAddressAction.LINK,
                link: {
                    customerId: cbCustomerId,
                    customerIdToLink: curr.customerId || null,
                }
            });
        });

        setQueryFields(customerFormToEditCustomerParams(params, cbContactPersonToDeleteArray, extraAddressesDelta));
    }

    const onReset = () => {

        if(isDirty) reset();

        setErrorSection({
            cbDetails: false,
            cbPersonalOrganizationInfo: false,
            cbInvoicingAddress: false,
            salesAreas: false,
            cbPaymentInfo: false,
            cbCorporateInfo: false
        });
        
        if(cbContactPersonToDeleteArray.length > 0)
            setCbContactPersonToDeleteArray([]);
        
        setIsGlobalEditMode(false);

    }

    const handleSubmit = () => {
        trigger().then((isValid) => {
            if(!isValid) {
                trigger("cbDetails").then((isValid) => {
                    setErrorSection((prev) => {
                        return {...prev, ["cbDetails"]: !isValid}
                    });
                });
                trigger("cbPersonalInfo").then((isValid) => {
                    setErrorSection((prev) => {
                        return {...prev, ["cbPersonalOrganizationInfo"]: !isValid}
                    });
                });
                trigger("cbInvoicingAddress").then((isValid) => {
                    setErrorSection((prev) => {
                        return {...prev, ["cbInvoicingAddress"]: !isValid}
                    });
                });
                trigger("salesAreas").then((isValid) => {
                    setErrorSection((prev) => {
                        return {...prev, ["salesAreas"]: !isValid}
                    });
                });
                trigger("cbPaymentInfo").then((isValid) => {
                    setErrorSection((prev) => {
                        return {...prev, ["cbPaymentInfo"]: !isValid}
                    });
                });

                if(getValues("cbDetails.partnerType") == CBPartnerType.ORGANIZATION) {
                    trigger("cbCorporateInfo").then((isValid) => {
                        setErrorSection((prev) => {
                            return {...prev, ["cbCorporateInfo"]: !isValid}
                        });
                    });
                }

            } else {
                setErrorSection({
                    cbDetails: false,
                    cbPersonalOrganizationInfo: false,
                    cbInvoicingAddress: false,
                    salesAreas: false,
                    cbPaymentInfo: false,
                    cbCorporateInfo: false
                });

                onSubmit(getValues());
            }
        });
    }

    return (
        <>
            {hasScope(CBAuth.WRITE_CUSTOMER) && isGlobalEditMode &&
                <Stack 
                    direction="row" 
                    alignItems={"center"} 
                    justifyContent={{
                        sm: "flex-end", 
                        xs: "flex-start"
                    }} 
                    columnGap={1} 
                    mb={{xs: 1}}
                >
                    <Button
                        variant="outlined"
                        color="accent"
                        onClick={onReset}
                    >
                        {t("tabs.buttonLabels.reset")}
                    </Button>
                    <Button
                        color="accent"
                        disabled={!isDirty}
                        onClick={handleSubmit}
                    >
                        {t("tabs.buttonLabels.save")}
                    </Button>                        
                </Stack>
            }
           { hasScope(CBAuth.WRITE_CUSTOMER) && !isGlobalEditMode &&    
                <Stack 
                    direction="row" 
                    alignItems={"center"} 
                    justifyContent={{
                        sm: "flex-end", 
                        xs: "flex-start"
                    }} 
                    columnGap={1} 
                    mb={{xs: 1}}
                >
                    <Button 
                        color="accent"
                        onClick={() => {
                            setIsGlobalEditMode(true);
                        }}
                    >
                        {t("tabs.buttonLabels.edit")}
                    </Button>
                </Stack>
            }
        </>

    )

}

export default CBMultiSectionActionButtons;